// VectorTileStyleToEsriRenderer.ts
// Helper class to convert Mapbox style layer symbology to Esri Maps SDK Renderer for GeoJSON layers

import type {
	LayerSpecification,
	FillLayerSpecification,
	LineLayerSpecification,
	SymbolLayerSpecification,
	CircleLayerSpecification,
} from 'mapbox-gl';
import type Renderer from '@arcgis/core/renderers/Renderer';
import SimpleRenderer from '@arcgis/core/renderers/SimpleRenderer';
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';
import Color from '@arcgis/core/Color.js';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol.js';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol.js';
import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol.js';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol.js';
import TextSymbol from '@arcgis/core/symbols/TextSymbol.js';
import LabelClass from '@arcgis/core/layers/support/LabelClass.js';

import icons from '@Map/symbols/icons';
import { GeoJsonDataType } from '@Map/services/types';
import SymbolLoader from '@Map/symbols/SymbolLoader';
import MapboxLayer from '@Map/layers/mapbox/MapboxLayer';
import {
	packSpriteFromSymbolLoader,
	pictureDataUrlFromSymbolLoader,
} from './rendererHelpers';
import {
	SpriteInfo,
	SpriteSourceImageInfo,
} from '@arcgis/core/layers/support/types';

import spriteImage from '../symbols/sprite/sprite.png';
import spriteImage2x from '../symbols/sprite/sprite@2x.png';
import spriteJson from '../symbols/sprite/sprite.json';
import spriteJson2x from '../symbols/sprite/sprite@2x.json';

/**
 * Converts a Mapbox LayerSpecification to an ArcGIS LabelClass when the layer
 * defines text labels (symbol layer with layout['text-field']). Returns null
 * for non-symbol layers or when no text-field is set.
 */
export function layerToLabelClass(
	layer: LayerSpecification,
): InstanceType<typeof LabelClass> | null {
	if (layer.type !== 'symbol') {
		return null;
	}
	const symbolLayer = layer as SymbolLayerSpecification;
	const textField = symbolLayer.layout?.['text-field'];
	const labelField = getLabelFieldFromTextField(textField);
	if (labelField == null) {
		return null;
	}
	const textColor = symbolLayer.paint?.['text-color'];
	const haloColor = symbolLayer.paint?.['text-halo-color'];
	const haloWidth = symbolLayer.paint?.['text-halo-width'];
	const color =
		typeof textColor === 'string' && !Array.isArray(textColor)
			? textColor
			: '#000000';
	const halo =
		typeof haloColor === 'string' && !Array.isArray(haloColor)
			? haloColor
			: null;
	const haloSize = typeof haloWidth === 'number' ? haloWidth : halo ? 1 : 0;
	const symbol = new TextSymbol({
		color: new Color(color),
		...(halo && { haloColor: new Color(halo), haloSize }),
		font: { size: 12, weight: 'bold' },
	});
	return new LabelClass({
		labelExpressionInfo: {
			expression: `return $feature["${labelField}"];`,
		},
		symbol,
	});
}

function getLabelFieldFromTextField(textField: unknown): string | null {
	if (!textField) return null;
	if (
		Array.isArray(textField) &&
		textField[0] === 'get' &&
		typeof textField[1] === 'string'
	) {
		return textField[1];
	}
	if (typeof textField === 'string') return textField;
	return null;
}

/**
 * Converts a Mapbox style layer to an Esri Renderer for GeoJSON layers.
 */
export class VectorTileStyleToEsriRenderer {
	/**
	 * Main entry point: converts a Mapbox layer definition to an Esri Renderer.
	 */
	static async convert(
		layer: LayerSpecification,
		data?: GeoJsonDataType,
		symbolLoader?: SymbolLoader,
	): Promise<Renderer | null> {
		switch (layer.type) {
			case 'fill':
				return this.fillToRenderer(layer as FillLayerSpecification);
			case 'line':
				return this.lineToRenderer(layer as LineLayerSpecification);
			case 'symbol':
				return await this.symbolToRenderer(
					layer as SymbolLayerSpecification,
					data,
					symbolLoader ?? new SymbolLoader(),
				);
			case 'circle':
				return this.circleToRenderer(layer as CircleLayerSpecification);
			default:
				return null;
		}
	}

	static circleToRenderer(layer: CircleLayerSpecification): SimpleRenderer {
		const color = Array.isArray(layer.paint?.['circle-color'])
			? '#000000'
			: (layer.paint?.['circle-color'] as string | undefined) ||
				'#000000';
		const outlineColor = Array.isArray(layer.paint?.['circle-stroke-color'])
			? '#000000'
			: (layer.paint?.['circle-stroke-color'] as string | undefined) ||
				'#000000';
		const outlineWidth =
			typeof layer.paint?.['circle-stroke-width'] === 'number'
				? layer.paint['circle-stroke-width']
				: 1;
		const radius =
			typeof layer.paint?.['circle-radius'] === 'number'
				? layer.paint['circle-radius']
				: 6;
		return new SimpleRenderer({
			symbol: new SimpleMarkerSymbol({
				style: 'circle',
				color: new Color(color),
				size: radius * 2,
				outline: {
					color: new Color(outlineColor),
					width: outlineWidth,
				},
			}),
		});
	}

	static fillToRenderer(layer: FillLayerSpecification): SimpleRenderer {
		const fillColor = Array.isArray(layer.paint?.['fill-color'])
			? MapboxLayer.UNSELECTED_COLOR_FILL
			: (layer.paint?.['fill-color'] as string | undefined) ||
				MapboxLayer.UNSELECTED_COLOR_FILL;
		const outlineColor = Array.isArray(layer.paint?.['fill-outline-color'])
			? MapboxLayer.UNSELECTED_COLOR_OUTLINE
			: (layer.paint?.['fill-outline-color'] as string | undefined) ||
				MapboxLayer.UNSELECTED_COLOR_OUTLINE;
		const color = new Color(fillColor);
		const opacity =
			typeof layer.paint?.['fill-opacity'] === 'number'
				? layer.paint['fill-opacity']
				: MapboxLayer.FILL_OPACITY;
		const fillColorAlpha =
			typeof color.a === 'number' && Number.isFinite(color.a) ? color.a : 1;
		// Mapbox combines both alpha channels: fill-color alpha * fill-opacity.
		color.a = fillColorAlpha * opacity;
		const outlineWidth =
			typeof (layer.paint as Record<string, unknown> | undefined)?.[
				'fill-outline-width'
			] === 'number'
				? ((layer.paint as Record<string, unknown>)['fill-outline-width'] as number)
				: 3;
		const outlineOpacity =
			typeof (layer.paint as Record<string, unknown> | undefined)?.[
				'fill-outline-opacity'
			] === 'number'
				? ((layer.paint as Record<string, unknown>)['fill-outline-opacity'] as number)
				: 1;
		const outline = new Color(outlineColor);
		const outlineColorAlpha =
			typeof outline.a === 'number' && Number.isFinite(outline.a)
				? outline.a
				: 1;
		outline.a = outlineColorAlpha * outlineOpacity;
		return new SimpleRenderer({
			symbol: new SimpleFillSymbol({
				color: color,
				outline: new SimpleLineSymbol({
					color: outline,
					width: outlineWidth,
				}),
			}),
		});
	}

	static lineToRenderer(layer: LineLayerSpecification): SimpleRenderer {
		const color = Array.isArray(layer.paint?.['line-color'])
			? MapboxLayer.UNSELECTED_COLOR_OUTLINE
			: (layer.paint?.['line-color'] as string | undefined) ||
				MapboxLayer.UNSELECTED_COLOR_OUTLINE;
		const width =
			typeof layer.paint?.['line-width'] === 'number'
				? layer.paint['line-width']
				: 3;
		return new SimpleRenderer({
			symbol: new SimpleLineSymbol({
				color: new Color(color),
				width,
			}),
		});
	}

	static async symbolToRenderer(
		layer: SymbolLayerSpecification,
		data: GeoJsonDataType | undefined,
		symbolLoader: SymbolLoader,
	): Promise<Renderer> {
		const iconImage = layer.layout?.['icon-image'];

		if (
			Array.isArray(iconImage) &&
			iconImage[0] === 'case' &&
			data?.features?.length
		) {
			let iconField: string | null = null;
			const hasArray = iconImage.find(
				item =>
					Array.isArray(item) &&
					item[0] === 'has' &&
					typeof item[1] === 'string',
			);
			if (hasArray) {
				iconField = hasArray[1];
			}
			const distinctValues = new Set<string>();
			data.features.forEach(feature => {
				const value = feature.properties?.[iconField || ''];
				if (typeof value === 'string') {
					distinctValues.add(value);
				}
			});
			const uniqueValueInfos = await Promise.all(
				Array.from(distinctValues).map(async val => {
					const url = await pictureDataUrlFromSymbolLoader(
						symbolLoader,
						val,
					);
					return {
						value: val,
						symbol: new PictureMarkerSymbol({
							url,
							width: 24,
							height: 24,
						}),
					};
				}),
			);
			const defaultExpr = iconImage[iconImage.length - 1];
			const defaultRef =
				typeof defaultExpr === 'string'
					? defaultExpr
					: String(defaultExpr);
			const defaultUrl = await pictureDataUrlFromSymbolLoader(
				symbolLoader,
				defaultRef,
			);
			return new UniqueValueRenderer({
				field: iconField || '',
				uniqueValueInfos,
				defaultSymbol: new PictureMarkerSymbol({
					url: defaultUrl,
					width: 24,
					height: 24,
				}),
			});
		} else if (typeof iconImage === 'string') {
			const url = await pictureDataUrlFromSymbolLoader(
				symbolLoader,
				iconImage,
			);
			return new SimpleRenderer({
				symbol: new PictureMarkerSymbol({
					url,
					width: 24,
					height: 24,
				}),
			});
		}

		return new SimpleRenderer({
			symbol: new SimpleMarkerSymbol({
				style: 'circle',
				color: 'red',
			}),
		});
	}

	/**
	 * Parsed parts of an icon-image string (e.g. "inno:manhole~#aabbcc" or
	 * "inno:manhole~#aabbcc-selected~#ff0000").
	 */
	static parseIconImage(iconImage: string): {
		namespace: string;
		iconName: string;
		color: string | null;
		selectedColor: string | null;
	} {
		const colonIndex = iconImage.indexOf(':');
		const namespace = colonIndex >= 0 ? iconImage.slice(0, colonIndex) : '';
		const afterNamespace =
			colonIndex >= 0 ? iconImage.slice(colonIndex + 1) : iconImage;
		const segments = afterNamespace.split('~').map(s => s.trim());
		const hexRe = /^#?([a-f0-9]{6}|[a-f0-9]{3})$/i;
		const toHex = (s: string): string => (s.startsWith('#') ? s : '#' + s);

		const iconName = (segments[0] ?? '')
			.replace(/-nobackground$/i, '')
			.replace(/-selected$/i, '')
			.replace(/-pattern$/i, '')
			.trim();

		let color: string | null = null;
		if (segments[1]) {
			const colorPart = segments[1]
				.replace(/-selected$/i, '')
				.replace(/-nobackground$/i, '')
				.trim();
			if (hexRe.test(colorPart) || hexRe.test('#' + colorPart)) {
				color = toHex(colorPart);
			}
		}

		let selectedColor: string | null = null;
		if (segments[2]) {
			// Strip -nobackground suffix before the hex test so transparency does
			// not break selectedColor parsing (e.g. "#ff5500-nobackground").
			const selectedColorPart = segments[2]
				.replace(/-nobackground$/i, '')
				.trim();
			if (
				hexRe.test(selectedColorPart) ||
				hexRe.test('#' + selectedColorPart)
			) {
				selectedColor = toHex(selectedColorPart);
			}
		}

		return { namespace, iconName, color, selectedColor };
	}

	/**
	 * Collects canonical sprite keys (`inno:…`) from every layer's
	 * `layout['icon-image']` by walking Mapbox expressions and string literals.
	 */
	static collectSpriteIconIdsFromStyleLayers(
		layers: readonly any[],
	): string[] {
		const ids = new Set<string>();
		for (const layer of layers) {
			const raw = layer?.layout?.['icon-image'];
			if (raw !== undefined && raw !== null) {
				this.walkIconImageExpressionForSpriteIds(raw, ids);
			}
		}
		return [...ids];
	}

	private static walkIconImageExpressionForSpriteIds(
		expr: unknown,
		ids: Set<string>,
	): void {
		if (typeof expr === 'string') {
			if (expr.includes('inno:')) {
				const parsed = this.parseIconImage(expr);
				const iconColor = parsed.color
					? parsed.color
					: MapboxLayer.UNSELECTED_COLOR_OUTLINE;
				const noBackground = expr.includes(
					SymbolLoader.disableBackgroundPosfix,
				);
				const bgSuffix = noBackground
					? SymbolLoader.disableBackgroundPosfix
					: '';
				const baseId = `inno:${parsed.iconName}~${iconColor}`;
				ids.add(`${baseId}${bgSuffix}`);
				if (parsed.selectedColor) {
					ids.add(
						`${baseId}-selected~${parsed.selectedColor}${bgSuffix}`,
					);
				}
			}
		} else if (Array.isArray(expr)) {
			if (expr.includes('concat') && expr.includes('inno:')) {
				// include all the icons
				const color = expr.find(e => e.includes('~'))?.split('~')[1];
				Object.keys(icons).forEach((icon: string) => {
					ids.add(`inno:${icon}~${color}`);
					if (icons[icon].includes('selected')) {
						ids.add(
							`inno:${icon}-selected~${MapboxLayer.SELECTED_COLOR}`,
						);
					}
				});
			} else {
				expr.forEach(e =>
					this.walkIconImageExpressionForSpriteIds(e, ids),
				);
			}
		}
	}

	/**
	 * Builds a new sprite sheet and JSON containing only the given icon ids,
	 * by copying rectangles from the full bundled sprite atlas.
	 */
	private static packSpriteSubset(
		fullJson: Record<string, SpriteInfo>,
		fullImage: HTMLImageElement,
		iconIds: string[],
		maxRowWidth = 4096,
		margin = 1,
	): { json: Record<string, SpriteInfo>; image: HTMLCanvasElement } {
		const uniqueIds = [...new Set(iconIds)].filter(id => fullJson[id]);
		if (uniqueIds.length === 0) {
			const canvas = document.createElement('canvas');
			canvas.width = 1;
			canvas.height = 1;
			return { json: {}, image: canvas };
		}

		uniqueIds.sort();

		type Placement = {
			id: string;
			src: SpriteInfo;
			dstX: number;
			dstY: number;
		};
		const placements: Placement[] = [];
		let x = 0;
		let y = 0;
		let rowH = 0;

		for (const id of uniqueIds) {
			const src = fullJson[id];
			const w = src.width;
			const h = src.height;
			if (x > 0 && x + w + margin > maxRowWidth) {
				x = 0;
				y += rowH + margin;
				rowH = 0;
			}
			placements.push({ id, src, dstX: x, dstY: y });
			x += w + margin;
			rowH = Math.max(rowH, h);
		}

		const canvasW = placements.reduce(
			(m, p) => Math.max(m, p.dstX + p.src.width),
			0,
		);
		const canvasH = placements.reduce(
			(m, p) => Math.max(m, p.dstY + p.src.height),
			0,
		);

		const canvas = document.createElement('canvas');
		canvas.width = canvasW;
		canvas.height = canvasH;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			throw new Error(
				'Could not get 2d context for sprite subset canvas',
			);
		}

		const newJson: Record<string, SpriteInfo> = {};
		for (const p of placements) {
			const { id, src, dstX, dstY } = p;
			ctx.drawImage(
				fullImage,
				src.x,
				src.y,
				src.width,
				src.height,
				dstX,
				dstY,
				src.width,
				src.height,
			);
			newJson[id] = {
				x: dstX,
				y: dstY,
				width: src.width,
				height: src.height,
				pixelRatio: src.pixelRatio,
				...(src.sdf !== undefined ? { sdf: src.sdf } : {}),
			};
		}
		return { json: newJson, image: canvas };
	}

	/**
	 * Sprite source built only from `layout['icon-image']` references in the
	 * given style layers. Rasters icons via {@link SymbolLoader#getImage} (theme +
	 * default SVGs). Falls back to copying from the bundled sprite PNG if loading
	 * fails or yields an empty atlas. Falls back to the full bundled sprite when
	 * no static `inno:` icon ids are found (e.g. only dynamic `icon-image`).
	 */
	static async createSpriteSourceImageInfoForStyleLayers(
		layers: readonly any[],
		symbolLoader: SymbolLoader,
	): Promise<SpriteSourceImageInfo> {
		const iconIds = this.collectSpriteIconIdsFromStyleLayers(layers);
		if (iconIds.length === 0) {
			return this.createSpriteSourceImageInfo();
		}

		const pixelRatio =
			typeof window !== 'undefined' && window.devicePixelRatio <= 1
				? 1
				: 2;

		try {
			const fromLoader = await packSpriteFromSymbolLoader(
				symbolLoader,
				iconIds,
				pixelRatio,
			);
			const packedJson =
				pixelRatio === 1 ? fromLoader.json1x : fromLoader.json2x;
			if (Object.keys(packedJson).length > 0) {
				return {
					type: 'image',
					pixelRatio: pixelRatio,
					spriteSource1x: {
						image: fromLoader.image1x,
						json: fromLoader.json1x,
					},
					spriteSource2x: {
						image: fromLoader.image2x,
						json: fromLoader.json2x,
					},
					spriteUrl: 'local://sprite',
				} as SpriteSourceImageInfo;
			}
		} catch {
			// fall through to bundled atlas subset
		}

		const fullJson1x = spriteJson as Record<string, SpriteInfo>;
		const fullJson2x = spriteJson2x as Record<string, SpriteInfo>;
		const [img1x, img2x] = await Promise.all([
			this.createLocalSpriteImage(spriteImage),
			this.createLocalSpriteImage(spriteImage2x),
		]);

		const packed1x = this.packSpriteSubset(fullJson1x, img1x, iconIds);
		const packed2x = this.packSpriteSubset(fullJson2x, img2x, iconIds);
		if (
			Object.keys(packed1x.json).length === 0 ||
			Object.keys(packed2x.json).length === 0
		) {
			return this.createSpriteSourceImageInfo();
		}

		return {
			type: 'image',
			pixelRatio: pixelRatio,
			spriteSource1x: {
				image: packed1x.image,
				json: packed1x.json,
			},
			spriteSource2x: {
				image: packed2x.image,
				json: packed2x.json,
			},
			spriteUrl: 'local://sprite',
		} as SpriteSourceImageInfo;
	}

	static async createLocalSpriteImage(
		path: string,
	): Promise<HTMLImageElement> {
		const response = await fetch(path);
		const blob = await response.blob();
		const dataUrl = await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});

		const img = new Image();
		img.src = dataUrl;

		return new Promise((resolve, reject) => {
			img.onload = () => resolve(img);
			img.onerror = reject;
		});
	}

	/**
	 * Creates SpriteSourceImageInfo from the bundled sprite image and JSON.
	 * Fetches the sprite image URL, converts it to a data URL, and returns
	 * an object suitable for VectorTileLayer.setSpriteSource().
	 */
	static async createSpriteSourceImageInfo(): Promise<SpriteSourceImageInfo> {
		const pixelRatio =
			typeof window !== 'undefined' && window.devicePixelRatio <= 1
				? 1
				: 2;
		const img = await this.createLocalSpriteImage(spriteImage);
		const img2x = await this.createLocalSpriteImage(spriteImage2x);

		return {
			type: 'image',
			pixelRatio: pixelRatio,
			spriteSource1x: {
				image: img,
				json: spriteJson,
			},
			spriteSource2x: {
				image: img2x,
				json: spriteJson2x,
			},
			spriteUrl: 'local://sprite', //arbitrary name, we don't need to use the full path
		} as SpriteSourceImageInfo;
	}
}
